import { describeMutationError } from "@api/mutationErrors";
import { useUser } from "@context/UserContext";
import { faUserTag } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, FieldError } from "@scribenote/brian-design-system";
import { RoleFieldset } from "@users/RoleFieldset";
import { type ClinicUser, isLeadershipRole } from "@users/userClient";
import { useSetUserRoles } from "@users/userQueries";
import { useState } from "react";

export const EditRolesButton = ({
  user,
  sessionKey,
}: {
  user: ClinicUser;
  sessionKey: string;
}) => {
  const { session } = useUser();
  const [open, setOpen] = useState(false);
  const held = user.roles.map((role) => role.id);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const setRoles = useSetUserRoles(sessionKey);
  // Mirrors brian's 409: a leader can't untick their own last leadership role.
  const leading = user.roles
    .filter(isLeadershipRole)
    .map((role) => role.id)
    .filter((id) => roleIds.includes(id));
  const locked =
    session?.userUuid === user.id && leading.length === 1 ? leading : [];

  // Held roles keep their order, so re-ticking one doesn't move the primary;
  // newly ticked roles follow in the order they were ticked.
  const choose = (next: string[]) =>
    setRoleIds([
      ...held.filter((id) => next.includes(id)),
      ...next.filter((id) => !held.includes(id)),
    ]);

  const start = () => {
    setRoleIds(held);
    setOpen(true);
  };

  const close = () => {
    setRoles.reset();
    setOpen(false);
  };

  // Order counts: the first role is the primary one.
  const unchanged = roleIds.join() === held.join();

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={start}
        data-testid={`admin-user-roles-${user.id}`}
      >
        <FontAwesomeIcon icon={faUserTag} aria-hidden="true" />
        Roles
      </Button>

      <Dialog
        open={open}
        onClose={close}
        size="sm"
        dismissable={!setRoles.isPending}
        title={
          // Dialog takes no testid; dropped while closed since a native <dialog> stays mounted.
          <span
            data-testid={open ? `admin-user-roles-modal-${user.id}` : undefined}
          >
            Roles for {user.displayName}
          </span>
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={close}
              disabled={setRoles.isPending}
              data-testid={`admin-user-roles-cancel-${user.id}`}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                setRoles.mutate(
                  { userId: user.id, roleIds },
                  { onSuccess: close },
                )
              }
              disabled={roleIds.length === 0 || unchanged}
              busy={setRoles.isPending}
              data-testid={`admin-user-roles-submit-${user.id}`}
            >
              {setRoles.isPending ? "Saving" : "Save roles"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-annotation">
            Takes effect on their next action. They stay signed in.
          </p>
          <RoleFieldset
            sessionKey={sessionKey}
            roleIds={roleIds}
            onChange={choose}
            disabled={setRoles.isPending}
            testIdPrefix={`admin-user-roles-role-${user.id}`}
            locked={locked}
          />
          {setRoles.isError && (
            <FieldError>
              {describeMutationError(setRoles.error, "change the roles")}
            </FieldError>
          )}
        </div>
      </Dialog>
    </>
  );
};
